import { ReactElement, useCallback, useMemo, memo } from 'react';
import { CircularProgress } from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRoute as RouteIcon,
  faBarcode as BardCodeIcon,
  faClone as DuplicateIcon,
  faMapMarkerAlt as FinalizeStopIcon,
} from '@fortawesome/free-solid-svg-icons';
import { isNull } from 'lodash';

import { PlanFeaturesTypes, RouteItemStateType } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { ItemExtended } from '~services/item/types';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { getUrlTrackingOrder, openUrlInNewTab } from '~utils/commons';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';
import MenuActionsDataTable from '~components/MenuActionsDataTable';
import { ShowIcon } from '~components/MenuActionsDataTable/styles';

import { useDashboardVariantV2CarrierContentInTransitTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

export interface DashboardVariantV2CarrierContentInTransitTabDataTableActionsMenuProps {
  data: ItemExtended;
  onExporTicket: (trackingCode: string) => void;
  loadingExportTicket: boolean;
}

const DashboardVariantV2CarrierContentInTransitTabDataTableActionsMenu = ({
  data,
  loadingExportTicket,
  onExporTicket,
}: DashboardVariantV2CarrierContentInTransitTabDataTableActionsMenuProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isCreatorOrderCurrentUser = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
      changeStateOrder: { handleOpen: handleOpenDialogChangeStateOrder },
      setFeedbackStop: { handleOpen: handleOpenDialogSetFeedbackStop },
      modifyAssignmentOrder: {
        handleOpen: handleOpenDialogModifyAssignmentOrder,
      },
    },
  } = useDashboardVariantV2CarrierContentInTransitTabContext();

  const handleRedirectToNewOrder = useCallback(
    (extraData?: Record<string, unknown>) => {
      navigate(PATHS.ORDERS.NEW, { state: extraData });
    },
    [navigate],
  );

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isCreatorOrderCurrentUser && data.canModify,
    [isUserOnlyReader, isCreatorOrderCurrentUser, data.canModify],
  );

  const enabledChangeOrder = useMemo(
    () => !isUserOnlyReader && !data.routeCode,
    [data.routeCode, isUserOnlyReader],
  );

  const isOwnItem = useMemo(
    () => isNull(data.carrierCompanyId),
    [data.carrierCompanyId],
  );

  const isOwnCompany = useMemo(
    () =>
      isCurrentLoggedCompany(data.carrierCompanyId, user?.companyId ?? null),
    [data.carrierCompanyId, user?.companyId],
  );

  const enabledShowRoute = useMemo(() => {
    if (!data.routeCode) return false;

    return !isCreatorOrderCurrentUser && (isOwnItem || isOwnCompany);
  }, [data.routeCode, isCreatorOrderCurrentUser, isOwnCompany, isOwnItem]);

  const pathShowRoute = useMemo(
    () =>
      replaceParamsPath(PATHS.ROUTES.VEHICLE_PLANNING, {
        ':apiTransactionId': data.apiTransactionId as string,
        ':routeId': data.routeId as string,
      }),
    [data.apiTransactionId, data.routeId],
  );

  const enabledFinalizedStop = useMemo(() => {
    if (!data.routeCode) return false;

    return (
      !isUserOnlyReader &&
      !isCreatorOrderCurrentUser &&
      data.itemStateTypeId === RouteItemStateType.InProgress
    );
  }, [
    data.routeCode,
    data.itemStateTypeId,
    isUserOnlyReader,
    isCreatorOrderCurrentUser,
  ]);

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ITEM_KEY_ID],
      }}
      items={{
        show: {
          enabled: true,
          onClick: () =>
            handleOpenDialogDetailsOrder({
              orderId: data[ITEM_KEY_ID],
              currentTab: null,
            }),
          divider: !enabledEdit,
        },
        edit: {
          enabled: enabledEdit,
          divider: true,
        },
      }}
      extraItems={[
        {
          enabled: enabledChangeOrder,
          label: 'Cambiar estado',
          Icon: <ChangeStateIcon color="success" />,
          onClick: () =>
            handleOpenDialogChangeStateOrder({
              itemsIds: [data[ITEM_KEY_ID]],
              excludeStateOption: data.itemStateTypeId,
            }),
        },
        {
          enabled: !isUserOnlyReader,
          label: 'Modificar asignación',
          Icon: <ModifyAssignmentIcon color="success" />,
          onClick: () =>
            handleOpenDialogModifyAssignmentOrder(data[ITEM_KEY_ID]),
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          enabled: enabledShowRoute,
          label: 'Ver ruta',
          Icon: <ShowIcon />,
          resource: { path: pathShowRoute },
        },
        {
          enabled: true,
          label: 'Ver seguimiento de órden',
          Icon: <FontAwesomeIcon icon={RouteIcon} />,
          onClick: () => {
            openUrlInNewTab(getUrlTrackingOrder(data.trackingCode));
          },
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.TrackingPortalModule,
          },
        },
        {
          enabled: true,
          label: 'Exportar etiqueta',
          Icon: loadingExportTicket ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={BardCodeIcon} />
          ),
          onClick: () => onExporTicket(data.trackingCode),
          divider: enabledEdit,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          enabled: enabledEdit,
          label: 'Duplicar',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          onClick: () =>
            handleRedirectToNewOrder({ duplicateById: data[ITEM_KEY_ID] }),
        },
        {
          enabled: enabledFinalizedStop,
          label: 'Finalizar parada',
          Icon: <FontAwesomeIcon icon={FinalizeStopIcon} />,
          onClick: () => handleOpenDialogSetFeedbackStop(data),
        },
      ]}
    />
  );
};

export default memo(
  DashboardVariantV2CarrierContentInTransitTabDataTableActionsMenu,
);
